// components/auth/ForgotPassword.js
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { forgotPassword, clearError, clearPasswordResetState } from '../../slices/authSlice.mjs';
import { setPageTitle, setPageDescription } from '../../slices/pageMetadataSlice.mjs';
import { setHeaderData } from '../../slices/headerSlice.mjs';

import useIsMounted from '../../hooks/useIsMounted.mjs';

const ForgotPasswordContent = () => {
    const [email, setEmail] = useState('');
    const [validationErrors, setValidationErrors] = useState({});

    const { loading, error, passwordResetEmailSent } = useSelector((state) => state.auth || {});
    const dispatch = useDispatch();
    const isMounted = useIsMounted();

    useEffect(() => {
        dispatch(setPageTitle('Forgot Password - Nexus of Truth'));
        dispatch(setPageDescription('Reset your Nexus of Truth password.'));
        dispatch(
            setHeaderData({
                title: 'Forgot Password',
                description: 'Reset your password.',
            })
        );

        // Clear password reset state when component mounts
        dispatch(clearPasswordResetState());

        // Clear errors when component unmounts
        return () => {
            if (error) {
                dispatch(clearError());
            }
        };
    }, [dispatch, error]);

    const handleChange = (e) => {
        setEmail(e.target.value);

        // Clear error when user starts typing
        if (validationErrors.email) {
            setValidationErrors({});
        }
        if (error) {
            dispatch(clearError());
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = 'Email is invalid';
        }

        setValidationErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        dispatch(forgotPassword(email));
    };

    return (
        <>
            <div className="auth-container">
                <div className="auth-form-wrapper">
                    <h1>Forgot Password</h1>

                    {!passwordResetEmailSent ? (
                        <>
                            <p>
                                Enter your email address and we'll send you a link to reset your
                                password.
                            </p>

                            {error && <div className="error-message">{error}</div>}

                            <form onSubmit={handleSubmit}>
                                <div className="form-group">
                                    <label htmlFor="email">Email Address</label>
                                    <input
                                        type="email"
                                        id="email"
                                        value={email}
                                        onChange={handleChange}
                                        className={validationErrors.email ? 'error' : ''}
                                        required
                                    />
                                    {validationErrors.email && (
                                        <div className="error-text">{validationErrors.email}</div>
                                    )}
                                </div>

                                <button type="submit" className="submit-button" disabled={loading}>
                                    {loading ? 'Sending...' : 'Send Reset Link'}
                                </button>
                            </form>
                        </>
                    ) : (
                        <div className="success-message">
                            <h2>Check Your Email</h2>
                            <p>
                                If that email address is in our system, we have sent you a password
                                reset link.
                            </p>
                            <p>If you don't see the email, check your spam folder.</p>
                        </div>
                    )}

                    <div className="auth-links">
                        {isMounted ? (
                            <Link to="/login">Back to Login</Link>
                        ) : (
                            <a href="/login">Back to Login</a>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ForgotPasswordContent;
